import React, { useState } from "react";
import {
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  Paper,
  IconButton,
  Grid,
  Alert
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import CustomButton from "../../components/CustomButton/Button";

const apiEndpoint = process.env.REACT_APP_API_BASE_URL;

const EncryptDecryptTool: React.FC = () => {
  const [mode, setMode] = useState<"encrypt" | "decrypt">("encrypt");
  const [input, setInput] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMode(event.target.value as "encrypt" | "decrypt");
    setResult("");
    setError("");
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInput(event.target.value);
    setResult("");
    setError("");
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    setResult("");
    try {
      const url = `${apiEndpoint}/utilities/${mode}`;
      const accessToken = localStorage.getItem("accessToken") || "";
      const response = await axios.post(
        url,
        { data: input },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `bearer ${accessToken}`
          }
        }
      );
      const resData = response.data;
      setResult(
        resData.EncryptedData || resData.DecryptedData || resData.data || ""
      );
    } catch (err: any) {
      setError("Failed to process.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (result) {
      navigator.clipboard.writeText(result);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      {error && (
        <Box mb={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Encrypt / Decrypt Tool
      </Typography>
      <Grid container spacing={3} direction="column">
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel>Choose Action</FormLabel>
            <RadioGroup value={mode} onChange={handleModeChange}>
              <FormControlLabel
                value="encrypt"
                control={<Radio />}
                label="Encrypt"
              />
              <FormControlLabel
                value="decrypt"
                control={<Radio />}
                label="Decrypt"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={mode === "encrypt" ? "Text to Encrypt" : "Text to Decrypt"}
            value={input}
            onChange={handleInputChange}
            variant="outlined"
            placeholder={
              mode === "encrypt"
                ? "Enter text to encrypt"
                : "Enter text to decrypt"
            }
            multiline
            minRows={2}
          />
        </Grid>
      </Grid>
      {result && (
        <Box mt={3}>
          <Typography variant="subtitle2">Result:</Typography>
          <Paper
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              background: "#f5f5f5",
              mt: 1
            }}
          >
            <Typography sx={{ wordBreak: "break-all", flex: 1 }}>
              {result}
            </Typography>
            <IconButton onClick={handleCopy} size="small">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            {copied && (
              <Typography variant="caption" color="primary" ml={1}>
                Copied!
              </Typography>
            )}
          </Paper>
        </Box>
      )}
      <Box display="flex" mt={3}>
        <CustomButton callBack={handleSubmit} disabled={loading || !input}>
          {loading
            ? mode === "encrypt"
              ? "Encrypting..."
              : "Decrypting..."
            : mode === "encrypt"
            ? "Encrypt"
            : "Decrypt"}
        </CustomButton>
      </Box>
    </Paper>
  );
};

export default EncryptDecryptTool;
